import Badge, { BadgeBg } from 'components/base/Badge';
import React, { useState } from 'react';
import { Card, Col, Row, Dropdown } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmployeeAvatarDropdown } from 'components/common/AvatarDropdown';
import EmployeeAvatar from 'components/base/Avatar';
import CardViewModal from './CardViewModal';
import {
  faChevronRight,
  faListCheck,
  faMapLocation,
  faMapLocationDot,
  faEllipsisVertical
} from '@fortawesome/free-solid-svg-icons';
import { Job, UPDATE_ARCHIVE_JOB_STAGE } from 'services/api/job';
import CustomerView from './CustomerView';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { Link } from 'react-router-dom';
import { CommentType } from 'services/api/comment';
import { JobStage } from 'services/api/jobStage';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { shortenText } from 'helpers/utils';
import { GET_FILTERED_TASKS } from 'services/api/task';

interface ArchiveCardViewItemProps {
  project: Job;
  JobStages: JobStage[];
  getJobStage: boolean;
  canMoveToActiveJob?: boolean;
  canUpdateArchiveJob?: boolean;
}

const ArchiveCardViewItem: React.FC<ArchiveCardViewItemProps> = ({
  project,
  JobStages,
  getJobStage,
  canMoveToActiveJob,
  canUpdateArchiveJob
}) => {
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const user = JSON.parse(localStorage.getItem('authUser') as string);

  const queryClient = useQueryClient();

  const { data: jobTasks, isLoading: isLoadingTasks } = useQuery(
    ['jobTasks', project?._id],
    () => GET_FILTERED_TASKS({ jobId: project?._id }),
    {
      enabled: !!project?._id && openDetailsModal,
      staleTime: 60 * 1000
    }
  );

  const getStatusBadgeColor = (status: string): BadgeBg => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return 'danger';
    if (statusLower === 'closed') return 'danger';
    return 'primary';
  };

  const updateJobStatus = useMutation(UPDATE_ARCHIVE_JOB_STAGE, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
    },
    onError: (error: any) => {
      toast.error(error.response.data.message || 'Failed to update job status');
    }
  });

  const handleStatusChange = async (jobId: string, prevStatus: string) => {
    if (prevStatus.toLowerCase() === 'closed') {
      const openTasks = project?.tasks?.filter(task => !task.completed);
      if (openTasks && openTasks.length > 0) {
        toast.error(
          'This job has 1 or more open tasks. Please complete the tasks and then change the job status to closed.'
        );
        return;
      }
    }

    let targetStage;
    if (getJobStage) {
      targetStage = JobStages?.find(
        stage => stage.name.toLowerCase() === prevStatus.toLowerCase()
      );
    }

    if (!targetStage) {
      toast.error(`Job stage "${prevStatus}" not found`);
      return;
    }

    const payload = {
      jobId: jobId,
      jobStage: { _id: targetStage.id }
    };

    try {
      await updateJobStatus.mutateAsync(payload);
      toast.success('Job status updated successfully');
    } catch (error: any) {
      console.error(
        error?.response?.data?.message || 'Failed to update job status'
      );
    }
  };

  return (
    <>
      <Card className="h-100 hover-actions-trigger">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="mb-2 line-clamp-1 lh-sm flex-1 me-5">
              {project.title} - Well: {project?.well}
            </h4>
            <div className="d-flex gap-2">
              <div className="hover-actions top-0 end-0 mt-4 me-4">
                {canMoveToActiveJob && (
                  <Dropdown align="start">
                    <Dropdown.Toggle
                      variant="falcon-default"
                      className="btn-icon dropdown-caret-none"
                    >
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => {
                          handleStatusChange(
                            project?._id as string,
                            project?.lastActiveJobStage?.name
                          );
                        }}
                      >
                        Move to Active
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                {canUpdateArchiveJob && (
                  <Button
                    variant="primary"
                    className="btn-icon flex-shrink-0"
                    onClick={() => setOpenDetailsModal(true)}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Badge
            variant="phoenix"
            bg={getStatusBadgeColor(project?.jobStage?.name)}
            className="fs-10 mb-4"
          >
            {project?.jobType?.name} - {project.jobStage?.name}
          </Badge>

          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon
              icon={faMapLocation}
              className="me-2 text-700 fs-9 fw-extra-bold"
            />
            <p className="fw-bold mb-0 text-truncate lh-1">
              Location:{' '}
              <span className="fw-semi-bold text-primary ms-1">
                <CustomerView customer={project.location.location} />
              </span>
            </p>
          </div>

          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon
              icon={faMapLocationDot}
              className="me-2 text-700 fs-9 fw-extra-bold"
            />
            <p className="fw-bold mb-0 text-truncate lh-1">
              Well:{' '}
              <span className="fw-semi-bold text-primary ms-1">
                <CustomerView customer={project.well} />
              </span>
            </p>
          </div>

          <div>
            <div className="d-flex align-items-center mt-4">
              <p className="mb-0 fw-bold fs-9">
                Job Number:
                <span className="fw-semi-bold text-600 ms-1">
                  {project.jobNumber ? project.jobNumber : 'No number set'}
                </span>
              </p>
            </div>
            <div className="d-flex align-items-center mt-4">
              <p className="mb-0 fw-bold fs-9">
                Description:
                <span className="fw-semi-bold text-600 ms-1">
                  {project.description ?? shortenText(project.description, 30)}
                </span>
              </p>
            </div>

            {/* Latest Comment Section */}
            <div className="d-flex mt-4 flex-column">
              <h4 className="mb-0 fw-bold fs-9">Latest Comment:</h4>
              <br />
              {project.comments && project.comments.length > 0 ? (
                <Row className="justify-content-between g-0 py-3 align-items-start">
                  <Col xs={12} lg={12}>
                    <p className="fs-9 text-800 mb-0">
                      <Link
                        to="#!"
                        className="fw-semi-bold text-decoration-none"
                      >
                        {project.comments[project.comments.length - 1].creator
                          ._id === user?._id
                          ? 'You'
                          : `${
                              project.comments[project.comments.length - 1]
                                .creator?.firstName
                            } ${
                              project.comments[project.comments.length - 1]
                                .creator?.lastName
                            }`}
                      </Link>{' '}
                      <span
                        className={`badge bg-${
                          project.comments[project.comments.length - 1]
                            .commentType === CommentType.Field
                            ? 'success'
                            : 'info'
                        }`}
                      >
                        {
                          project.comments[project.comments.length - 1]
                            .commentType
                        }
                      </span>
                    </p>
                    <p className="text-800 fw-semi-bold fs-10 mb-0">
                      <HumanReadableTime
                        date={
                          new Date(
                            project.comments[project.comments.length - 1]
                              .createdAt as Date
                          )
                        }
                      />
                    </p>
                    {project.comments[project.comments.length - 1].content && (
                      <p className="mt-2">
                        {project.comments[project.comments.length - 1].content}
                      </p>
                    )}
                  </Col>
                </Row>
              ) : (
                <p>No Comments Added</p>
              )}
            </div>

            <div className="d-flex d-lg-block d-xl-flex justify-content-between align-items-center mt-3">
              <div className="d-flex gap-1">
                <EmployeeAvatar.Group
                  total={project?.assignedTo?.length}
                  size="m"
                >
                  {project?.assignedTo?.slice(0, 4).map(assigned => (
                    <EmployeeAvatarDropdown
                      user={assigned}
                      jobId={project?._id}
                      supervisorId={project?.supervisor[0]?._id}
                      size="m"
                      key={assigned?._id}
                    />
                  ))}
                </EmployeeAvatar.Group>
              </div>

              <div className="mt-lg-3 mt-xl-0">
                <FontAwesomeIcon icon={faListCheck} className="me-1" />
                <p className="d-inline-block fw-bold mb-0">
                  {project?.tasks?.length}
                  <span className="fw-normal"> Task</span>
                </p>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <CardViewModal
        show={openDetailsModal}
        handleClose={() => setOpenDetailsModal(false)}
        project={project as unknown as Job}
        isLoadingTasks={isLoadingTasks}
        jobTasks={jobTasks?.data}
      />
    </>
  );
};

export default ArchiveCardViewItem;
